import React from "react";
import BaseComponent, {executeComponentCallback} from 'Core/components/BaseComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {getPageActions} from 'Core/helpers/redux';
import * as actions from './actions';
import {fetchStudyInfoAction} from '../../../pages/apps/default/studies/popups/PatientStudyInfoPopup/actions';
import {reducerStoreKey} from './reducer';
import {selectors} from 'Core/store/reducers';
import * as filterDataMap from './dataMap/filter';
import {areAllObjectPropsEmpty, getArray, getString, trimArray} from 'Core/helpers/data';
import {Tooltip} from 'Core/components/global/Tooltip';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import ConfirmDialog from 'Core/components/dialogs/ConfirmDialog';
import {isSuccessful} from 'Core/helpers/io';
import {getAppDateString, STANDARD_DATE_TIME_FORMAT} from 'Core/helpers/datetime';
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import Label from 'Core/components/display/Label';
import DataTable, {DATA_TABLE_CELL_TYPE} from 'Core/components/advanced/DataTable';
import {PAGINATION_TYPE} from 'Core/components/action/Pagination';
import {getAppLocaleDateFormat} from 'Core/helpers/locale';
import {LOCALE_DATE_FORMAT_NAME} from 'Core/const/locale';
import {icon_font_error_symbol, icon_font_loading_symbol} from 'Config/app';
import {QUESTIONNAIRE_VERSION} from 'Const/questionnaire';
import Separator from 'Core/components/display/Separator';

/**
 * Define all actions that can be used by this component.
 */
const allActions = getPageActions({...actions, fetchStudyInfoAction});

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in the component.
 */
const mapStateToProps = state => ({
	mainList: selectors[reducerStoreKey].getUserSubmittedQuestionnairesList(state),
	mainListPagination: selectors[reducerStoreKey].getUserSubmittedQuestionnairesListPagination(state),
	mainListSort: selectors[reducerStoreKey].getUserSubmittedQuestionnairesListSort(state),
	mainListFilter: selectors[reducerStoreKey].getUserSubmittedQuestionnairesListFilter(state),
});

class UserSubmittedQuestionnaires extends BaseComponent {
	constructor(props) {
		super(props, {
			translationPath: 'UserSubmittedQuestionnaires',
			domPrefix: 'user-submitted-questionnaires-table',
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onAttachSuccess', ...Object.keys(allActions)],
		});

		// Initiate component's state
		this.state = {
			showUserSubmittedQuestionnairesList: false,
			/** @type {?PatientStudyInfoDataObject|undefined} */
			study: null,
		};

		// Refs
		this.mainListFilterRef = null;

		// Data methods
		this.loadStudyLocally = this.loadStudyLocally.bind(this);
		this.loadUserSubmittedQuestionnairesList = this.loadUserSubmittedQuestionnairesList.bind(this);
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);
		
		// Action methods
		this.attachMainListItem = this.attachMainListItem.bind(this);
		
		// Render methods
		this.renderActions = this.renderActions.bind(this);
	}
	
	componentWillUnmount() {
		super.componentWillUnmount();
		
		const {clearUserSubmittedQuestionnairesListAction} = this.props;
		
		clearUserSubmittedQuestionnairesListAction();
	}


	// Component property methods ---------------------------------------------------------------------------------------
	/**
	 * Get component's ID that can be used as a DOM element id attribute value
	 * @return {string}
	 */
	getDomId() { return this.getOption('domPrefix'); }
	

	// Data methods -----------------------------------------------------------------------------------------------------
	/** @inheritDoc */
	async asyncComponentDidMount() {
		await super.asyncComponentDidMount();
		
		const {accountId, studyId, loadUserSubmittedQuestionnairesListAction} = this.props;
		
		await this.executeAbortableActionMount(loadUserSubmittedQuestionnairesListAction, accountId, studyId)
			.then(res => this.setState({showUserSubmittedQuestionnairesList: getArray(res, 'data').length > 0}));

		// Load study info in the background because it is used only in the dialog confirming the assignment of the 
		// user-submitted study. User does not need to know or wait for it when this component is mounted. Loading overlay
		// will be shown only if needed and only related to the dialog confirming the assignment.
		if (this.state.showUserSubmittedQuestionnairesList) this.loadStudyLocally(true).then();
	}

	/**
	 * Load study data into the components' local state
	 * 
	 * @param {boolean} [onMount=false] - Flag indicating that the method is called during the component's mount cycle.
	 * @return {Promise<Object>}
	 */
	loadStudyLocally(onMount = false) {
		const {studyId, fetchStudyInfoAction} = this.props;
		const executeAbortableAction = (onMount ? this.executeAbortableActionMount : this.executeAbortableAction);
		
		return executeAbortableAction(fetchStudyInfoAction, studyId)
			.then(res => isSuccessful(res) ? res.data : undefined)
			.then(study => this.setState({study}));
	}
	
	/**
	 * Load the user-submitted questionnaires list
	 * 
	 * @param {Object} [oFilter] - Filter object ready for output.
	 * @param {number} [pageNo] - Page number.
	 * @param {number} [perPage] - Number of items per page.
	 * @param {string} [sortBy] - Sort by field.
	 * @param {SortOrder|''} [sortDir] - Sort direction.
	 */
	loadUserSubmittedQuestionnairesList(oFilter, pageNo, perPage, sortBy, sortDir) {
		const {accountId, studyId, loadUserSubmittedQuestionnairesListAction} = this.props;

		return this.executeAbortableAction(
			loadUserSubmittedQuestionnairesListAction, accountId, studyId, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}
	
	/**
	 * Reload the main list using current options (page, sort, ...)
	 * @return {Promise<*>}
	 */
	reloadMainList() {
		const {mainListPagination, mainListSort, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.loadUserSubmittedQuestionnairesList(oFilter, pageNo, perPage, sortBy, sortDir)
			.then(res => {
				this.mainListFilterRef?.reload();
				return res;
			});
	}

	/**
	 * Reload the main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {mainListPagination, mainListSort, mainListFilter} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.loadUserSubmittedQuestionnairesList(oFilter, pageNo, perPage, sortBy, sortDir);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {mainListPagination, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.loadUserSubmittedQuestionnairesList(oFilter, pageNo, perPage, sortBy, sortDir);
	}

	/**
	 * Filter the main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {mainListPagination, mainListSort} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(filter);

		return this.loadUserSubmittedQuestionnairesList(oFilter, 1, perPage, sortBy, sortDir);
	}

	/**
	 * Remove the main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if the main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}
	
	
	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Attach main list item
	 *
	 * @param {UserSubmittedQuestionnairesListItemDataObject} item - Main list popup item.
	 * @return {Promise<void>}
	 */
	attachMainListItem(item) {
		const {studyId, openDialogAction, closeDialogAction, attachUserSubmittedQuestionnaireAction} = this.props;
		/** @type {?PatientStudyInfoDataObject|undefined} */
		const study = this.state.study;
		
		const dialogGUIID = openDialogAction('', ConfirmDialog, {
			message: this.t('confirm_attach', '', '', {
				firstName: study.patientFirstName,
				lastName: study.patientLastName,
				dateOfBirth: getAppDateString(study.patientDateOfBirth),
				submittedFirstName: item.firstName,
				submittedLastName: item.lastName,
				submittedDateOfBirth: getAppDateString(item.dateOfBirth),
				gender: (!!study.patientGender ? this.t(study.patientGender, 'constants.gender') : '—'),
				submittedGender: (!!item.gender ? this.t(item.gender, 'constants.gender') : '—'),
			}),
			supportHtml: true,
			onYes: () => {
				this.executeAbortableAction(attachUserSubmittedQuestionnaireAction, studyId, item.id)
					.then(response => {
						if (!!response) {
							executeComponentCallback(this.props.onAttachSuccess, QUESTIONNAIRE_VERSION.IMPORT);
							closeDialogAction(dialogGUIID);
						}
						return response;
					});
			},
		}, {
			id: 'item-attach-dialog',
			closeOnEscape: false,
			closeOnClickOutside: false,
			hideCloseBtn: false,
			maxWidth: 720
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render data table actions cell
	 * 
	 * @param {UserSubmittedQuestionnairesListItemDataObject} row - Data table row.
	 * @param {?PatientStudyInfoDataObject|undefined} study - Patient study info.
	 * @return {JSX.Element}
	 */
	renderActions(row, study) {
		return (
			<div className="actions">
				{study === null ?
					<Tooltip
						tag="div"
						title={this.t('attach_wait_tooltip')}
						size="small"
						position="top-center"
						arrow={true}
						interactive={false}
					>
						<Button
							displayStyle={BUTTON_STYLE.ACTION}
							displayType={BUTTON_DISPLAY_TYPE.SOLID}
							icon={icon_font_loading_symbol}
							spinIcon={true}
							label={this.t('attach')}
							disabled={true}
						/>
					</Tooltip>
				: typeof study === 'undefined' ?
					<Tooltip
						tag="div"
						title={this.t('attach_study_error_tooltip')}
						size="small"
						position="top-center"
						arrow={true}
						interactive={false}
					>
						<Button
							displayStyle={BUTTON_STYLE.ERROR}
							displayType={BUTTON_DISPLAY_TYPE.SOLID}
							icon={icon_font_error_symbol}
							label={this.t('attach')}
							disabled={true}
						/>
					</Tooltip>
				:
					<Tooltip
						tag="div"
						title={this.t('attach_tooltip')}
						size="small"
						position="top-center"
						arrow={true}
						interactive={false}
					>
						<Button
							displayStyle={BUTTON_STYLE.ACTION}
							displayType={BUTTON_DISPLAY_TYPE.SOLID}
							icon="cloud-download"
							label={this.t('attach')}
							onClick={() => this.attachMainListItem(row)}
						/>
					</Tooltip>
				}
			</div>
		);
	}
	
	render() {
		const {className, mainList, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {showUserSubmittedQuestionnairesList} = this.state;
		/** @type {?PatientStudyInfoDataObject|undefined} */
		const study = this.state.study;
		
		if (!showUserSubmittedQuestionnairesList) return null;
		return (
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')} ${getString(className)}`}>
				<Separator className="separator" content={`... ${this.t('import_user_submitted_title')}:`} />
				<SimpleStaticSearch
					styleName="compact"
					className="user-submitted-questionnaires-search rows-2"
					defaultCollapse={true}
					layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
					buttonProps={{
						displayStyle: BUTTON_STYLE.DEFAULT
					}}
					options={[
						new SimpleStaticSearchOptionObject(
							'firstName',
							this.t('firstNameField')
						),
						new SimpleStaticSearchOptionObject(
							'lastName',
							this.t('lastNameField')
						),
						new SimpleStaticSearchOptionObject(
							'dateOfBirth',
							this.t('dateOfBirthField'),
							SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
							{
								valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
							}
						),
						new SimpleStaticSearchOptionObject(
							'dateQuestionnaireSubmitted',
							this.t('dateOfSubmissionField'),
							SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
							{
								valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
							}
						),
					]}
					value={mainListFilter}
					title={(<Label icon="search" content={this.t('Search', 'general')} />)}
					applied={!this.isMainListFilterEmpty()}
					enableToolbar={true}
					enableResetButton={false}
					onChange={this.filterMainList}
					onRemove={this.removeMainListFilter}
					ref={node => { this.mainListFilterRef = node; }}
				/>

				<DataTable
					id="user-submitted-questionnaires-table"
					className="standard sticky-last-column"
					highlightOnHover={true}
					columns={[
						{
							name: 'firstName',
							sortName: 'firstName',
							label: this.t('firstNameField'),
						},
						{
							name: 'lastName',
							sortName: 'lastName',
							label: this.t('lastNameField'),
						},
						{
							name: 'dateOfBirth',
							sortName: 'dateOfBirth',
							label: this.t('dateOfBirthField'),
							dataType: DATA_TABLE_CELL_TYPE.DATE,
							dataTypeOptions: {
								outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.STANDARD),
								whiteSpace: 'nowrap',
							},
						},
						{
							name: 'dateOfSubmission',
							sortName: 'dateQuestionnaireSubmitted',
							label: this.t('dateOfSubmissionField'),
							dataType: DATA_TABLE_CELL_TYPE.DATE,
							dataTypeOptions: {
								outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.STANDARD),
								whiteSpace: 'nowrap',
							},
						},

						{
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: {
								standardWrapper: false,
								content: row => this.renderActions(row, study),
							},
							stopPropagation: true,
							width: 1,
						}
					]}
					data={mainList}
					paginationType={PAGINATION_TYPE.DYNAMIC}
					onSortByColumn={this.sortMainList}
					onPaginationClick={this.loadMainListPage}
					{...mainListPagination}
					{...mainListSort}
				/>
			</div>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
UserSubmittedQuestionnaires.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	
	accountId: PropTypes.string,
	studyId: PropTypes.string,
	
	onAttachSuccess: PropTypes.func, // {QuestionnaireVersion} version
};

/**
 * Define component default values for own props
 */
UserSubmittedQuestionnaires.defaultProps = {
	id: '',
	className: '',
};

export default connect(mapStateToProps, allActions, null, {forwardRef: true})(UserSubmittedQuestionnaires);